/**
 * @file Форматы элементов, проверка имён и навигация внутри /home.
 *
 * Непроверенный JSON не передаётся напрямую в экран. Сортировка возвращает
 * новый массив, чтобы не менять список, который уже хранится в состоянии React.
 */
import { validateFolderName } from '../folders/folderName.ts'

export type FileErrorCode =
    | 'emptyName'
    | 'reservedName'
    | 'invalidName'
    | 'alreadyExists'
    | 'forbidden'
    | 'notFound'
    | 'failed'
    | 'unconfirmed'

export class FileOperationError extends Error {
    readonly code: FileErrorCode

    constructor(code: FileErrorCode) {
        super(code)
        this.name = 'FileOperationError'
        this.code = code
    }
}

export function fileErrorCode(error: unknown): FileErrorCode {
    return error instanceof FileOperationError ? error.code : 'unconfirmed'
}

export function validateRenameName(
    value: string,
    currentName: string,
    existingNames: ReadonlySet<string>,
): FileErrorCode | null {
    const names = new Set(existingNames)

    // Собственное имя не является конфликтом при сохранении без изменения названия.
    names.delete(currentName)

    return validateFolderName(value, names)
}

export interface FolderItem {
    name: string
    kind: 'file' | 'folder'
}

export interface FolderContent {
    items: FolderItem[]
}

export function parseFolderContent(value: unknown): FolderContent {
    if (
        !value ||
        typeof value !== 'object' ||
        !('items' in value) ||
        !Array.isArray(value.items)
    )
        throw new FileOperationError('unconfirmed')

    const names = new Set<string>()
    const items = value.items.map((item: unknown): FolderItem => {
        if (
            !item ||
            typeof item !== 'object' ||
            !('name' in item) ||
            typeof item.name !== 'string' ||
            !item.name ||
            item.name === '.' ||
            item.name === '..' ||
            /[/\\\0]/.test(item.name) ||
            names.has(item.name) ||
            !('kind' in item) ||
            (item.kind !== 'file' && item.kind !== 'folder')
        )
            throw new FileOperationError('unconfirmed')

        names.add(item.name)

        return { name: item.name, kind: item.kind }
    })

    return { items }
}

export function sortFolderItems(
    items: readonly FolderItem[],
    locale: string,
): FolderItem[] {
    const collator = new Intl.Collator(locale, {
        numeric: true,
        sensitivity: 'base',
    })

    return [...items].sort((a, b) =>
        a.kind !== b.kind
            ? a.kind === 'folder'
                ? -1
                : 1
            : collator.compare(a.name, b.name),
    )
}

export function validFolderPath(value: string | null): string {
    if (!value || (value !== '/home' && !value.startsWith('/home/')))
        return '/home'

    return value
        .split('/')
        .slice(1)
        .every(
            (part) =>
                part && part !== '.' && part !== '..' && !/[\\\0]/.test(part),
        )
        ? value
        : '/home'
}

export function folderBreadcrumbs(
    path: string,
): { name: string; path: string }[] {
    const parts = validFolderPath(path).split('/').slice(1)

    return parts.map((name, index) => ({
        name,
        path: '/' + parts.slice(0, index + 1).join('/'),
    }))
}

export function checkFileResponse(response: Response): void {
    if (response.ok) return

    if (response.status === 409) throw new FileOperationError('alreadyExists')

    if (response.status === 400 || response.status === 422)
        throw new FileOperationError('invalidName')

    if (response.status === 401 || response.status === 403)
        throw new FileOperationError('forbidden')

    if (response.status === 404) throw new FileOperationError('notFound')

    throw new FileOperationError('unconfirmed')
}
